<script lang="ts" setup>
import { useExperimentsStore } from '@smg-real-estate/openfeature-vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  PhoneNumberInput,
  Space,
  TransitionWrapper,
} from '@/components/common';
import ToastMessage from '@/components/common/ToastMessage/ToastMessage.vue';
import useQueryParamTreatment, { ABnFeaturesQuery } from '@/composables/useQueryParamTreatment';
import { useResultNavigation } from '@/composables/useResultNavigation';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';

import { isPostalCodeInExperimentRegion } from '@/helpers/AgentRecommendations/agentRecommendationExperimentConfig';
import { getAgentRecommendationsForPostalCode } from '@/helpers/AgentRecommendations/agentRecommendations';
import { OfferType } from '@/models/valuationFunnel';
import {
  PP_3474_SplitFeatureFlags_Treatments,
  SplitFeatureFlags,
} from '@/splitExperiments/splitFeatureFlags';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { trackingSellerLeadSubmit } from '@/tracking/tracking';
import { BREAKPOINTS, HttpStatusCode, ToastType, ValuationFlow } from '@/types';

// Props
interface Props {
  title?: string;
  backButtonVisible?: boolean;
  descriptionVisible?: boolean;
}

const redirectAfterSubmission = ref(false);

const props = withDefaults(defineProps<Props>(), {
  title: '',
  backButtonVisible: true,
  descriptionVisible: true,
});

// Composables
const experimentsStore = useExperimentsStore();
const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  loading: formLoading,
  step: formStep,
} = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
const { handleRoute, getStepInputsForTracking, setToastType } = useResultNavigation();

// State
const phoneNumberData = reactive<{
  valid: boolean | undefined;
  number: string | null;
}>({
  valid: true,
  number: null,
});

const showToast = ref(false);

// Computed
const submissionInProgress = computed(() => redirectAfterSubmission.value || formLoading.value);

const phoneNumber = computed({
  get(): string | null {
    return phoneNumberData.number;
  },
  set(value: any): void {
    if (value) {
      Object.assign(phoneNumberData, value);
    }
  },
});

// Validations
const rules = {
  phoneNumber: {
    required,
  },
  phoneNumberData: {
    formatValid: (phoneObject: { valid: boolean | undefined }) => {
      return phoneObject.valid;
    },
  },
};

const v$ = useVuelidate(rules, { phoneNumber, phoneNumberData });

// Methods
const getUtmValue = (id: string): string | null => {
  const input = document.getElementById(id) as HTMLInputElement | null;
  return input?.value ? input.value : null;
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.PhoneNumber);
};

const getToastType = (statusCode: HttpStatusCode | undefined): ToastType => {
  if (statusCode && statusCode === HttpStatusCode.TooManyRequests) {
    return ToastType.TooManyRequests;
  } else {
    return ToastType.ServerFailure;
  }
};

const showSubmitFailureToast = (statusCode: HttpStatusCode): void => {
  setToastType(getToastType(statusCode));
  showToast.value = true;
};

const queryTreatment = useQueryParamTreatment(ABnFeaturesQuery.PP_3474);
const PP_3474_Treatment = computed(() => experimentsStore.getTreatment(SplitFeatureFlags.PP_3474));

const shouldDirectToAgentsRecommendation = () => {
  const isPostalCodeInExperiment =
    formData.value.address.postalCode &&
    isPostalCodeInExperimentRegion(formData.value.address.postalCode);
  const isSellOfferType = formData.value?.offerType === OfferType.Selling;
  const isAtLeastThreeAgents =
    getAgentRecommendationsForPostalCode(formData.value.address.postalCode).length >= 3;

  return (
    isPostalCodeInExperiment &&
    isSellOfferType &&
    isAtLeastThreeAgents &&
    (queryTreatment.value ===
      PP_3474_SplitFeatureFlags_Treatments.REQUIRED_SELECTION_AGENT_RECOMENDATIONS ||
      queryTreatment.value ===
        PP_3474_SplitFeatureFlags_Treatments.OPTIONAL_SELECTION_AGENT_RECOMMENDATIONS ||
      PP_3474_Treatment.value?.treatment ===
        PP_3474_SplitFeatureFlags_Treatments.REQUIRED_SELECTION_AGENT_RECOMENDATIONS ||
      PP_3474_Treatment.value?.treatment ===
        PP_3474_SplitFeatureFlags_Treatments.OPTIONAL_SELECTION_AGENT_RECOMMENDATIONS)
  );
};

const submit = async (): Promise<void> => {
  v$.value.$touch();

  if (v$.value.$invalid) {
    return;
  }

  propertyValuationStore.setFormData({
    phoneNumber: phoneNumber.value ?? '',
    culture: `${locale.value}-CH`,
  });

  if (shouldDirectToAgentsRecommendation()) {
    propertyValuationStore.navigateNext(ValuationFlow.PhoneNumber);
    return;
  }

  redirectAfterSubmission.value = true;
  const { isSuccess, result, statusCode } = await propertyValuationStore.submitQualifiedValuation();

  if (isSuccess && result) {
    propertyValuationStore.setFormData({ leadId: result.leadId });
    propertyValuationStore.setLeadFromQualifiedValuation(result);

    if (formData.value?.offerType === OfferType.Selling) {
      trackingSellerLeadSubmit(result.leadId, {
        h_event_label_008: 0,
      });
    }

    // Navigate directly to result page
    setToastType(ToastType.SubmitSuccess);
    showToast.value = true;
    await handleRoute(result.leadId);
  } else {
    redirectAfterSubmission.value = false;
    showSubmitFailureToast(statusCode as HttpStatusCode);
  }
};

// Lifecycle
onMounted(async () => {
  // Wait for next tick to ensure Vuelidate has fully initialized
  await nextTick();

  // Reset the form state to clear any initial dirty flags
  // This prevents errors from showing on initial render
  v$.value.$reset();

  trackVirtualPageViewWithTreatment({
    subType: 'input_phoneNumber',
    locale: locale.value,
    stepInputs: getStepInputsForTracking(),
  });

  const utmSource = getUtmValue('utm_source');
  const utmMedium = getUtmValue('utm_medium');
  const utmCampaign = getUtmValue('utm_campaign');
  propertyValuationStore.setFormData({
    utmSource: utmSource ?? '',
    utmMedium: utmMedium ?? '',
    utmCampaign: utmCampaign ?? '',
  });
});
</script>

<template>
  <FormHeader :formStep="formStep" :showBackBtn="backButtonVisible" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <form ref="phoneNumberForm" novalidate @submit.prevent="submit">
        <FormTitle :class="$style.title">{{ title || t('numberVerification.title') }}</FormTitle>
        <FormContent>
          <Space direction="vertical">
            <p v-if="descriptionVisible" :class="$style.description">
              {{ t('numberVerification.description') }}
            </p>
            <PhoneNumberInput
              ref="inputPhone"
              v-model="v$.phoneNumber.$model as any"
              name="phoneNumber"
              :label="t('numberVerification.numberLabel')"
              inputmode="numeric"
              :isDirty="v$.phoneNumber.$dirty || v$.phoneNumberData.$dirty"
              :isInvalid="v$.phoneNumber.$invalid || v$.phoneNumberData.$invalid"
            >
              <template
                v-if="v$.phoneNumber.$invalid || v$.phoneNumberData.$invalid"
                #errorMessages
              >
                <span v-if="v$.phoneNumber.$dirty && v$.phoneNumber.required.$invalid">
                  {{
                    t('errorMessage.requiredPhone', {
                      fieldName: t('numberVerification.numberLabel'),
                    })
                  }}
                </span>
                <span v-else-if="v$.phoneNumberData.formatValid.$invalid">
                  {{
                    t('errorMessage.invalidPhone', {
                      fieldName: t('numberVerification.numberLabel'),
                    })
                  }}
                </span>
              </template>
            </PhoneNumberInput>
          </Space>
        </FormContent>
        <FormBottom
          :nextDisabled="submissionInProgress"
          :nextButtonLabel="t('button.confirmValuation')"
          :isSubmit="true"
        />
      </form>
    </TransitionWrapper>
    <ToastMessage
      v-if="showToast"
      :notificationWidth="BREAKPOINTS.funnel_container_max_width.toString() + 'px'"
    />
  </FormContainer>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.title {
  padding-bottom: $space-150;
}

.description {
  margin-top: 0;
  margin-bottom: $space-250;

  @include body-2($color-grey-120);
}
</style>
